import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Plus, Trash2, Send, Landmark, CheckCircle2, Search } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { maskAccount } from "../lib/format";

export default function BankAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState(null);
  const [banks, setBanks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [inquiry, setInquiry] = useState(null);
  const [inquiring, setInquiring] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/bank-accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));

  useEffect(() => {
    load();
    api.get("/banks").then((r) => setBanks(r.data));
  }, []);

  const doInquiry = async () => {
    if (!bankCode || !accountNumber) {
      toast.error("Pilih bank dan isi nomor rekening dulu");
      return;
    }
    setInquiring(true);
    setInquiry(null);
    try {
      const res = await api.post("/banks/inquiry", { bank_code: bankCode, account_number: accountNumber });
      setInquiry(res.data);
    } catch (err) {
      toast.error(errMsg(err, "Rekening tidak valid"));
    } finally {
      setInquiring(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/bank-accounts", { bank_code: bankCode, account_number: accountNumber });
      toast.success("Rekening berhasil disimpan");
      setDialogOpen(false);
      setBankCode("");
      setAccountNumber("");
      setInquiry(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan rekening"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bank-accounts/${id}`);
      toast.success("Rekening dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menghapus rekening"));
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Rekening Bank</h1>
          <p className="text-sm text-muted-foreground">Rekening tersimpan untuk transfer lebih cepat.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setInquiry(null); setBankCode(""); setAccountNumber(""); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-bank-account-button">
              <Plus className="h-4 w-4 mr-1.5" /> Tambah
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Tambah Rekening Bank</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>Bank</Label>
                <Select value={bankCode} onValueChange={(v) => { setBankCode(v); setInquiry(null); }}>
                  <SelectTrigger className="h-11" data-testid="add-account-bank-select">
                    <SelectValue placeholder="Pilih bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.code} value={b.code}>{b.name} — {b.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nomor Rekening</Label>
                <Input
                  data-testid="add-account-number-input"
                  className="h-11 font-mono"
                  inputMode="numeric"
                  placeholder="Contoh: 1234567890"
                  value={accountNumber}
                  onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, "")); setInquiry(null); }}
                />
              </div>
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5" onClick={doInquiry} disabled={inquiring} data-testid="add-account-check-button">
                {inquiring ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Search className="h-4 w-4 mr-1.5" /> Cek Nama</>)}
              </Button>
              {inquiry && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="add-account-inquiry-result">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">{inquiry.account_holder}</p>
                    <p className="text-xs text-muted-foreground">{inquiry.bank_name} · {maskAccount(inquiry.account_number)}</p>
                  </div>
                </div>
              )}
              <Button className="w-full h-11" onClick={save} disabled={!inquiry || saving} data-testid="add-account-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Rekening"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {accounts === null ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}
        </div>
      ) : accounts.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="text-center py-14" data-testid="bank-accounts-empty-state">
            <div className="mx-auto h-14 w-14 rounded-full bg-secondary flex items-center justify-center mb-3">
              <Landmark className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">Belum ada rekening tersimpan</p>
            <p className="text-xs text-muted-foreground mt-1">Tambahkan rekening bank untuk transfer lebih cepat.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {accounts.map((acc, i) => (
            <motion.div key={acc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.05 }}>
              <Card className="rounded-2xl" data-testid="bank-account-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <span className="h-11 w-11 rounded-xl bg-blue-50 text-primary font-bold text-xs flex items-center justify-center shrink-0">
                    {acc.bank_name?.slice(0, 4).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{acc.account_holder}</p>
                    <p className="text-xs text-muted-foreground font-mono">{acc.bank_name} · {acc.account_number}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="border-primary text-primary hover:bg-primary/5" onClick={() => navigate("/transfer/bank", { state: { account: acc } })} data-testid="bank-account-transfer-button">
                      <Send className="h-3.5 w-3.5 mr-1" /> Transfer
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" aria-label="Hapus rekening" data-testid="bank-account-delete-button">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Hapus rekening ini?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {acc.account_holder} — {acc.bank_name} {maskAccount(acc.account_number)} akan dihapus dari daftar tersimpan.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={() => remove(acc.id)} data-testid="confirm-delete-account-button">
                            Hapus
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
